import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { App, Button, Modal, Select, Space, Table, Tag, Typography, Form, InputNumber, Radio, Input } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiError } from '@/lib/api';
import { formatDate, formatDateTime, formatRupiah } from '@/lib/format';
import { useAuthStore } from '@/lib/auth-store';
const TYPE_COLORS = {
    PURCHASE: 'blue',
    SALE: 'green',
    ADJUSTMENT_IN: 'cyan',
    ADJUSTMENT_OUT: 'orange',
    OPENING: 'purple',
};
export function StockMovementsPage() {
    const { message } = App.useApp();
    const qc = useQueryClient();
    const hasPerm = useAuthStore((s) => s.hasPerm);
    const [productFilter, setProductFilter] = useState();
    const [typeFilter, setTypeFilter] = useState();
    const [adjModal, setAdjModal] = useState(false);
    const [form] = Form.useForm();
    const products = useQuery({
        queryKey: ['toko-products-select'],
        queryFn: async () => (await api.get('/toko/products', { params: { active: true } })).data,
    });
    const movs = useQuery({
        queryKey: ['toko-movements', productFilter, typeFilter],
        queryFn: async () => (await api.get('/toko/stock/movements', {
            params: { product_id: productFilter, movement_type: typeFilter },
        })).data,
    });
    const adjMut = useMutation({
        mutationFn: async (v) => (await api.post('/toko/stock/adjust', v)).data,
        onSuccess: () => {
            message.success('Stok disesuaikan');
            qc.invalidateQueries({ queryKey: ['toko-movements'] });
            qc.invalidateQueries({ queryKey: ['toko-products'] });
            setAdjModal(false);
        },
        onError: (e) => message.error(apiError(e)),
    });
    const cols = [
        { title: 'Waktu', dataIndex: 'created_at', width: 160, render: (d) => formatDateTime(d) },
        { title: 'Tanggal', dataIndex: 'tx_date', width: 110, render: (d) => formatDate(d) },
        {
            title: 'Tipe', dataIndex: 'movement_type', width: 130,
            render: (t) => _jsx(Tag, { color: TYPE_COLORS[t], children: t }),
        },
        { title: 'SKU', dataIndex: 'product_sku', width: 110 },
        { title: 'Produk', dataIndex: 'product_name' },
        {
            title: 'Qty', dataIndex: 'qty', width: 110, align: 'right',
            render: (v) => _jsx("span", { className: "money", children: parseFloat(v).toFixed(3) }),
        },
        {
            title: 'Unit Cost', dataIndex: 'unit_cost', width: 140, align: 'right',
            render: (v) => v ? _jsx("span", { className: "money", children: formatRupiah(v) }) : '—',
        },
        { title: 'Referensi', dataIndex: 'reference_type', width: 110 },
        {
            title: 'Jurnal', dataIndex: 'journal_id', width: 100,
            render: (id) => id ? _jsx(Link, { to: `/accounting/journals/${id}`, children: "Lihat" }) : '—',
        },
        { title: 'Catatan', dataIndex: 'notes' },
    ];
    return (_jsxs("div", { children: [_jsxs(Space, { style: { marginBottom: 16, display: 'flex', justifyContent: 'space-between', width: '100%' }, children: [_jsx(Typography.Title, { level: 3, style: { margin: 0 }, children: "Pergerakan Stok" }), hasPerm('stock:adjust') && (_jsx(Button, { type: "primary", icon: _jsx(PlusOutlined, {}), onClick: () => { form.resetFields(); form.setFieldsValue({ direction: 'IN', qty: 1 }); setAdjModal(true); }, children: "Adjustment Stok" }))] }), _jsxs(Space, { style: { marginBottom: 16 }, wrap: true, children: [_jsx(Select, { allowClear: true, showSearch: true, style: { width: 260 }, placeholder: "Filter produk", optionFilterProp: "label", value: productFilter, onChange: (v) => setProductFilter(v), options: (products.data ?? []).map((p) => ({ value: p.id, label: `${p.sku} — ${p.name}` })) }), _jsx(Select, { allowClear: true, style: { width: 200 }, placeholder: "Tipe pergerakan", value: typeFilter, onChange: (v) => setTypeFilter(v), options: Object.keys(TYPE_COLORS).map((t) => ({ value: t, label: t })) })] }), _jsx(Table, { rowKey: "id", loading: movs.isLoading, dataSource: movs.data ?? [], columns: cols, pagination: { pageSize: 40 }, size: "middle" }), _jsx(Modal, { title: "Adjustment Stok", open: adjModal, onCancel: () => setAdjModal(false), onOk: () => form.validateFields().then((v) => adjMut.mutate({
                    product_id: v.product_id,
                    qty: v.qty?.toString(),
                    direction: v.direction,
                    unit_cost: v.unit_cost?.toString(),
                    notes: v.notes,
                })), confirmLoading: adjMut.isPending, children: _jsxs(Form, { form: form, layout: "vertical", children: [_jsx(Form.Item, { name: "product_id", label: "Produk", rules: [{ required: true }], children: _jsx(Select, { showSearch: true, optionFilterProp: "label", options: (products.data ?? []).map((p) => ({ value: p.id, label: `${p.sku} — ${p.name}` })) }) }), _jsx(Form.Item, { name: "direction", label: "Arah", rules: [{ required: true }], children: _jsxs(Radio.Group, { children: [_jsx(Radio.Button, { value: "IN", children: "Masuk (+)" }), _jsx(Radio.Button, { value: "OUT", children: "Keluar (\u2212)" })] }) }), _jsx(Form.Item, { name: "qty", label: "Jumlah", rules: [{ required: true }], children: _jsx(InputNumber, { style: { width: '100%' }, min: 0, step: 0.1 }) }), _jsx(Form.Item, { name: "unit_cost", label: "Harga Beli (opsional, hanya utk arah IN)", children: _jsx(InputNumber, { style: { width: '100%' }, min: 0, formatter: (v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.'), parser: ((v) => parseFloat((v ?? '').replace(/\./g, '')) || 0) }) }), _jsx(Form.Item, { name: "notes", label: "Alasan", children: _jsx(Input.TextArea, { rows: 2 }) })] }) })] }));
}
